import type { TFunction } from 'i18next';
import { ApiError, errorCodes } from '../api/http';

/**
 * The machine codes the client can state better, in the reader's language, than the server's English
 * detail can. Everything else keeps the server's wording: an unmapped detail names the actual file
 * or tenant involved, which a generic translated sentence would throw away.
 */
export const errorCodeMessageKeys = {
  [errorCodes.tenantNotRegistered]: 'errors.tenantNotRegistered',
  [errorCodes.tenantDisabled]: 'errors.tenantDisabled',
  [errorCodes.roleNotAssigned]: 'errors.roleNotAssigned',
  [errorCodes.tenantNotSelected]: 'errors.tenantNotSelected',
  [errorCodes.noTenantAssigned]: 'errors.noTenantAssigned',
  [errorCodes.tenantSchemaNotReady]: 'errors.tenantSchemaNotReady',
  [errorCodes.tenantNotSelectable]: 'errors.tenantNotSelectable',
  [errorCodes.tenantSchemaProvisioning]: 'errors.tenantSchemaProvisioning',
  [errorCodes.codeNotInCodelist]: 'errors.codeNotInCodelist',
} as const;

type MappedErrorCode = keyof typeof errorCodeMessageKeys;

/** Kept literal rather than widened to string, so `t` still typechecks the key it is handed. */
function messageKeyFor(code: string | undefined) {
  return code !== undefined && code in errorCodeMessageKeys
    ? errorCodeMessageKeys[code as MappedErrorCode]
    : undefined;
}

/**
 * Turns anything thrown into something worth showing. Deliberately not part of `describeError` in
 * the API layer: that stays free of translation so it can keep being a pure reader of the response.
 */
export function describeErrorLocalized(error: unknown, t: TFunction): string {
  if (error instanceof ApiError) {
    if (error.status === 401) return t('errors.sessionExpired');

    const key = messageKeyFor(error.errorCode);
    if (key !== undefined) return t(key);

    return error.problem?.detail ?? error.problem?.title ?? error.message;
  }

  if (error instanceof Error) return error.message;

  return t('errors.somethingWentWrong');
}
